import { supabase } from '@/lib/supabase';
import { checkRateLimit, rateLimitMessage } from '@/lib/coreRateLimit';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 43. SEARCH ENGINE INTERNE  &  44. GLOBAL SEARCH
// Couche de recherche centralisée. On peut chercher : clients,
// entreprises, produits, services, documents, commandes, factures,
// rendez-vous et réservations.
//
// ⚠️ Règle d'or : la recherche RESPECTE LES PERMISSIONS. Un utilisateur
// ne doit jamais découvrir une ressource simplement parce qu'elle existe
// dans l'index. Chaque type d'entité est donc associé à une permission
// requise ; les résultats des types non autorisés sont retirés AVANT
// l'affichage.
// ─────────────────────────────────────────────────────────────

export interface SearchEntityDef {
  key: string;
  label: string;
  icon: string;
  className: string;
  /** Permission requise pour voir ce type de résultat. */
  permission: string;
}

export const SEARCH_ENTITY_TYPES: SearchEntityDef[] = [
  { key: 'customer', label: 'Clients', icon: 'ri-user-star-line', className: 'bg-primary-100 text-primary-900', permission: 'customers.read' },
  { key: 'business', label: 'Entreprises', icon: 'ri-building-2-line', className: 'bg-secondary-100 text-secondary-900', permission: 'settings.read' },
  { key: 'product', label: 'Produits', icon: 'ri-shopping-bag-3-line', className: 'bg-accent-100 text-accent-900', permission: 'products.read' },
  { key: 'service', label: 'Services', icon: 'ri-service-line', className: 'bg-secondary-100 text-secondary-900', permission: 'services.read' },
  { key: 'document', label: 'Documents', icon: 'ri-file-text-line', className: 'bg-background-100 text-foreground-700', permission: 'content.read' },
  { key: 'order', label: 'Commandes', icon: 'ri-file-list-3-line', className: 'bg-accent-100 text-accent-900', permission: 'orders.read' },
  { key: 'invoice', label: 'Factures', icon: 'ri-bill-line', className: 'bg-primary-100 text-primary-900', permission: 'finance.read' },
  { key: 'appointment', label: 'Rendez-vous', icon: 'ri-calendar-check-line', className: 'bg-primary-100 text-primary-900', permission: 'services.read' },
  { key: 'reservation', label: 'Réservations', icon: 'ri-hotel-line', className: 'bg-accent-100 text-accent-900', permission: 'products.read' },
  { key: 'accommodation', label: 'Hébergements', icon: 'ri-home-4-line', className: 'bg-secondary-100 text-secondary-900', permission: 'products.read' },
];

export function searchEntityMeta(key: string | null | undefined): SearchEntityDef {
  return (
    SEARCH_ENTITY_TYPES.find((e) => e.key === key) || {
      key: 'other',
      label: 'Autres',
      icon: 'ri-search-line',
      className: 'bg-background-100 text-foreground-700',
      permission: '',
    }
  );
}

export interface CoreSearchEntry {
  id: number;
  tenant_id: number | null;
  organization_id: number | null;
  entity_type: string;
  entity_id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  keywords: string | null;
  url: string | null;
  module: string | null;
  icon: string | null;
  metadata: Record<string, unknown> | null;
  updated_at: string | null;
  created_at: string | null;
}

export interface SearchResult extends CoreSearchEntry {
  typeLabel: string;
  typeIcon: string;
  typeClass: string;
}

export interface SearchGroup {
  key: string;
  label: string;
  icon: string;
  className: string;
  results: SearchResult[];
}

/** Nettoie la requête pour éviter de casser la syntaxe du filtre OR. */
function sanitizeQuery(q: string): string {
  return q.replace(/[%,()*]/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * 43/44 — Recherche globale filtrée par permissions.
 * `canAccess(permission)` doit renvoyer true si l'utilisateur possède la
 * permission. Les entités non autorisées sont TOTALEMENT retirées.
 */
export async function searchGlobal(params: {
  organizationId: number;
  query: string;
  canAccess: (permission: string) => boolean;
  entityTypes?: string[];
  limit?: number;
  /** Utilisateur appelant : sert de sujet au rate limiting (spec 64). */
  userId?: number | null;
}): Promise<{ groups: SearchGroup[]; total: number; allowed: string[] }> {
  const q = sanitizeQuery(params.query);
  if (q.length < 2) return { groups: [], total: 0, allowed: [] };

  // Rate limiting applicatif par utilisateur (spec 64) : refuse au-delà de la
  // limite, journalise chaque passage dans core_rate_limit_events.
  const rl = await checkRateLimit({
    organizationId: params.organizationId,
    scope: 'search',
    subjectType: 'user',
    subjectKey: params.userId ? `user:${params.userId}` : `org:${params.organizationId}`,
    endpoint: 'search.global',
  });
  if (!rl.allowed) throw new Error(rateLimitMessage(rl));

  // Types autorisés selon les permissions de l'utilisateur.
  const allowed = SEARCH_ENTITY_TYPES.filter(
    (e) => (!params.entityTypes || params.entityTypes.includes(e.key)) && params.canAccess(e.permission),
  ).map((e) => e.key);

  if (allowed.length === 0) return { groups: [], total: 0, allowed: [] };

  const like = `%${q}%`;
  const { data, error } = await supabase
    .from('core_search_index')
    .select('*')
    .eq('organization_id', params.organizationId)
    .in('entity_type', allowed)
    .or(`title.ilike.${like},subtitle.ilike.${like},description.ilike.${like},keywords.ilike.${like}`)
    .order('updated_at', { ascending: false })
    .limit(params.limit ?? 80);

  if (error) throw error;

  const rows = (data || []) as CoreSearchEntry[];
  const decorated: SearchResult[] = rows.map((r) => {
    const meta = searchEntityMeta(r.entity_type);
    return { ...r, typeLabel: meta.label, typeIcon: meta.icon, typeClass: meta.className };
  });

  // Regroupe dans l'ordre du catalogue.
  const groups: SearchGroup[] = SEARCH_ENTITY_TYPES.filter((e) => allowed.includes(e.key))
    .map((e) => {
      const results = decorated.filter((r) => r.entity_type === e.key);
      return { key: e.key, label: e.label, icon: e.icon, className: e.className, results };
    })
    .filter((g) => g.results.length > 0);

  return { groups, total: decorated.length, allowed };
}

// ── Alimentation de l'index ──

export interface SearchEntryInput {
  tenantId: number | null;
  organizationId: number;
  entityType: string;
  entityId: string | number;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  keywords?: string | null;
  url?: string | null;
  module?: string | null;
  icon?: string | null;
  metadata?: Record<string, unknown> | null;
}

export async function upsertSearchEntry(input: SearchEntryInput): Promise<void> {
  const meta = searchEntityMeta(input.entityType);
  const { error } = await supabase
    .from('core_search_index')
    .upsert(
      {
        tenant_id: input.tenantId,
        organization_id: input.organizationId,
        entity_type: input.entityType,
        entity_id: String(input.entityId),
        title: input.title,
        subtitle: input.subtitle || null,
        description: input.description || null,
        keywords: input.keywords || null,
        url: input.url || null,
        module: input.module || input.entityType,
        icon: input.icon || meta.icon,
        metadata: input.metadata ?? null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'organization_id,entity_type,entity_id' },
    );
  if (error) throw error;
}

export async function removeSearchEntry(
  organizationId: number,
  entityType: string,
  entityId: string | number,
): Promise<void> {
  const { error } = await supabase
    .from('core_search_index')
    .delete()
    .eq('organization_id', organizationId)
    .eq('entity_type', entityType)
    .eq('entity_id', String(entityId));
  if (error) throw error;
}

export interface ReindexResult {
  indexed: number;
  byType: Record<string, number>;
}

/**
 * (Ré)construit l'index de recherche de l'organisation à partir des données
 * réelles des modules. Idempotent (upsert). Best-effort par source.
 */
export async function reindexOrganization(params: {
  tenantId: number | null;
  organizationId: number;
  commerceId: number;
}): Promise<ReindexResult> {
  const result: ReindexResult = { indexed: 0, byType: {} };
  const { tenantId, organizationId, commerceId } = params;

  const bump = (type: string, n: number) => {
    result.indexed += n;
    result.byType[type] = (result.byType[type] || 0) + n;
  };

  // Helper : lit puis upsert un lot.
  const run = async (
    type: string,
    reader: () => Promise<SearchEntryInput[]>,
  ): Promise<void> => {
    try {
      const entries = await reader();
      for (const e of entries) {
        try {
          await upsertSearchEntry(e);
        } catch {
          /* entrée ignorée */
        }
      }
      bump(type, entries.length);
    } catch {
      /* source indisponible : on continue */
    }
  };

  await run('customer', async () => {
    const { data } = await supabase
      .from('core_customers')
      .select('id, display_name, email, phone, company_name')
      .eq('organization_id', organizationId)
      .limit(2000);
    return (data || []).map((r) => {
      const c = r as { id: number; display_name: string | null; email: string | null; phone: string | null; company_name: string | null };
      return {
        tenantId, organizationId, entityType: 'customer', entityId: c.id,
        title: c.display_name || c.email || `Client #${c.id}`,
        subtitle: c.email || c.phone || null,
        keywords: [c.display_name, c.email, c.phone, c.company_name].filter(Boolean).join(' '),
        url: '/dashboard/customers',
        module: 'customers',
      };
    });
  });

  await run('order', async () => {
    const { data } = await supabase
      .from('commande')
      .select('id, nomclient, nom, email, tel, totalttc, monaie')
      .eq('idvendeur', commerceId)
      .limit(2000);
    return (data || []).map((r) => {
      const o = r as { id: number; nomclient: string | null; nom: string | null; email: string | null; tel: string | null; totalttc: string | null; monaie: string | null };
      return {
        tenantId, organizationId, entityType: 'order', entityId: o.id,
        title: `Commande #${o.id}`,
        subtitle: [o.nomclient || o.nom, o.totalttc ? `${o.totalttc} ${o.monaie || ''}`.trim() : null].filter(Boolean).join(' · ') || null,
        keywords: [o.nomclient, o.nom, o.email, o.tel, `#${o.id}`].filter(Boolean).join(' '),
        url: '/dashboard/orders',
        module: 'orders',
      };
    });
  });

  await run('invoice', async () => {
    const { data } = await supabase
      .from('factures')
      .select('id, numero, objet, statut, montant_ttc')
      .eq('idcommerce', commerceId)
      .limit(2000);
    return (data || []).map((r) => {
      const f = r as { id: number; numero: string | null; objet: string | null; statut: string | null; montant_ttc: string | null };
      return {
        tenantId, organizationId, entityType: 'invoice', entityId: f.id,
        title: f.numero ? `Facture ${f.numero}` : `Facture #${f.id}`,
        subtitle: [f.objet, f.statut].filter(Boolean).join(' · ') || null,
        keywords: [f.numero, f.objet, f.statut].filter(Boolean).join(' '),
        url: '/dashboard/finance/factures',
        module: 'finance',
      };
    });
  });

  await run('product', async () => {
    const { data } = await supabase
      .from('product_items')
      .select('id, name, price, currency, status, description')
      .eq('idcommerce', commerceId)
      .limit(2000);
    return (data || []).map((r) => {
      const p = r as { id: number; name: string | null; price: number | null; currency: string | null; status: string | null; description: string | null };
      return {
        tenantId, organizationId, entityType: 'product', entityId: p.id,
        title: p.name || `Produit #${p.id}`,
        subtitle: p.price != null ? `${p.price} ${p.currency || ''}`.trim() : null,
        description: p.description || null,
        keywords: [p.name, p.status].filter(Boolean).join(' '),
        url: '/dashboard/products',
        module: 'products',
      };
    });
  });

  await run('service', async () => {
    const { data } = await supabase
      .from('services')
      .select('id, titre, description, prix')
      .eq('idvendeur', commerceId)
      .limit(2000);
    return (data || []).map((r) => {
      const s = r as { id: number; titre: string | null; description: string | null; prix: string | null };
      return {
        tenantId, organizationId, entityType: 'service', entityId: s.id,
        title: s.titre || `Service #${s.id}`,
        subtitle: s.prix || null,
        keywords: [s.titre, s.description].filter(Boolean).join(' '),
        url: '/dashboard/services',
        module: 'services',
      };
    });
  });

  await run('document', async () => {
    const { data } = await supabase
      .from('documents')
      .select('id, name, doc_type')
      .eq('user_id', commerceId)
      .limit(2000);
    return (data || []).map((r) => {
      const d = r as { id: number; name: string | null; doc_type: string | null };
      return {
        tenantId, organizationId, entityType: 'document', entityId: d.id,
        title: d.name || `Document #${d.id}`,
        subtitle: d.doc_type || null,
        keywords: [d.name, d.doc_type].filter(Boolean).join(' '),
        url: '/dashboard/documents',
        module: 'documents',
      };
    });
  });

  await run('accommodation', async () => {
    const { data } = await supabase
      .from('hebergement_properties')
      .select('id, name, city, price, currency')
      .eq('merchant_id', commerceId)
      .limit(2000);
    return (data || []).map((r) => {
      const p = r as { id: number; name: string | null; city: string | null; price: string | null; currency: string | null };
      return {
        tenantId, organizationId, entityType: 'accommodation', entityId: p.id,
        title: p.name || `Hébergement #${p.id}`,
        subtitle: [p.city, p.price ? `${p.price} ${p.currency || ''}`.trim() : null].filter(Boolean).join(' · ') || null,
        keywords: [p.name, p.city].filter(Boolean).join(' '),
        url: '/dashboard/apps/hebergement',
        module: 'accommodation',
      };
    });
  });

  await run('reservation', async () => {
    const { data } = await supabase
      .from('hebergement_reservations')
      .select('id, guest_name, guest_email, guest_phone, check_in, check_out')
      .eq('merchant_id', commerceId)
      .limit(2000);
    return (data || []).map((r) => {
      const v = r as { id: number; guest_name: string | null; guest_email: string | null; guest_phone: string | null; check_in: string | null; check_out: string | null };
      return {
        tenantId, organizationId, entityType: 'reservation', entityId: v.id,
        title: v.guest_name || `Réservation #${v.id}`,
        subtitle: [v.check_in, v.check_out].filter(Boolean).join(' → ') || null,
        keywords: [v.guest_name, v.guest_email, v.guest_phone].filter(Boolean).join(' '),
        url: '/dashboard/apps/hebergement',
        module: 'accommodation',
      };
    });
  });

  await run('appointment', async () => {
    const { data: svc } = await supabase
      .from('booking_services')
      .select('id, service_name')
      .eq('owner', commerceId)
      .limit(500);
    const svcIds = (svc || []).map((s) => (s as { id: number }).id);
    if (svcIds.length === 0) return [];
    const { data } = await supabase
      .from('booking_appointments')
      .select('id, fullname, email, phone, date, time, service_id, status')
      .in('service_id', svcIds)
      .limit(2000);
    return (data || []).map((r) => {
      const a = r as { id: number; fullname: string | null; email: string | null; phone: string | null; date: string | null; time: string | null; service_id: number | null; status: string | null };
      return {
        tenantId, organizationId, entityType: 'appointment', entityId: a.id,
        title: a.fullname || `Rendez-vous #${a.id}`,
        subtitle: [a.date, a.time].filter(Boolean).join(' · ') || null,
        keywords: [a.fullname, a.email, a.phone].filter(Boolean).join(' '),
        url: '/dashboard/apps/zcalendar',
        module: 'booking',
      };
    });
  });

  return result;
}

// ── Historique & statistiques ──

export async function logSearch(input: {
  tenantId: number | null;
  organizationId: number;
  userId?: number | null;
  query: string;
  resultCount: number;
}): Promise<void> {
  try {
    await supabase.from('core_search_history').insert({
      tenant_id: input.tenantId,
      organization_id: input.organizationId,
      user_id: input.userId ?? null,
      query: input.query.slice(0, 200),
      result_count: input.resultCount,
    });
  } catch {
    /* best-effort */
  }
}

export interface CoreSearchHistory {
  id: number;
  query: string;
  result_count: number | null;
  created_at: string | null;
}

export async function listRecentSearches(organizationId: number, limit = 12): Promise<CoreSearchHistory[]> {
  const { data, error } = await supabase
    .from('core_search_history')
    .select('id, query, result_count, created_at')
    .eq('organization_id', organizationId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as CoreSearchHistory[];
}

export interface SearchStats {
  total: number;
  byType: Record<string, number>;
}

export async function fetchSearchStats(organizationId: number): Promise<SearchStats> {
  const { data } = await supabase
    .from('core_search_index')
    .select('entity_type')
    .eq('organization_id', organizationId);
  const byType: Record<string, number> = {};
  (data || []).forEach((r) => {
    const t = (r as { entity_type: string }).entity_type;
    byType[t] = (byType[t] || 0) + 1;
  });
  return { total: (data || []).length, byType };
}

// ── Réindexation à l'unité (déclenchée à chaque création/modification) ──

/** Types d'entités dont la réindexation à l'unité est prise en charge. */
export const REINDEXABLE_ENTITY_TYPES = [
  'customer', 'order', 'invoice', 'product', 'service', 'document',
  'accommodation', 'reservation', 'appointment',
] as const;

export interface ReindexOneParams {
  tenantId?: number | null;
  organizationId: number;
  commerceId?: number | null;
  entityType: string;
  entityId: string | number;
}

/**
 * 43 — Réindexe UNE seule entité (créée ou modifiée) dans l'index central.
 * Best-effort : lit l'entité dans sa table source et upsert l'entrée. Ne
 * lève jamais — renvoie false si introuvable ou type non pris en charge.
 */
export async function reindexOne(params: ReindexOneParams): Promise<boolean> {
  const { organizationId, entityId } = params;
  const tenantId = params.tenantId ?? null;
  const commerceId = params.commerceId ?? null;

  try {
    switch (params.entityType) {
      case 'customer': {
        const { data } = await supabase
          .from('core_customers')
          .select('id, display_name, email, phone, company_name')
          .eq('id', entityId)
          .maybeSingle();
        const c = data as { id: number; display_name: string | null; email: string | null; phone: string | null; company_name: string | null } | null;
        if (!c) return false;
        await upsertSearchEntry({
          tenantId, organizationId, entityType: 'customer', entityId: c.id,
          title: c.display_name || c.email || `Client #${c.id}`,
          subtitle: c.email || c.phone || null,
          keywords: [c.display_name, c.email, c.phone, c.company_name].filter(Boolean).join(' '),
          url: '/dashboard/customers', module: 'customers',
        });
        return true;
      }
      case 'order': {
        const { data } = await supabase
          .from('commande')
          .select('id, nomclient, nom, email, tel, totalttc, monaie')
          .eq('id', entityId)
          .maybeSingle();
        const o = data as { id: number; nomclient: string | null; nom: string | null; email: string | null; tel: string | null; totalttc: string | null; monaie: string | null } | null;
        if (!o) return false;
        await upsertSearchEntry({
          tenantId, organizationId, entityType: 'order', entityId: o.id,
          title: `Commande #${o.id}`,
          subtitle: [o.nomclient || o.nom, o.totalttc ? `${o.totalttc} ${o.monaie || ''}`.trim() : null].filter(Boolean).join(' · ') || null,
          keywords: [o.nomclient, o.nom, o.email, o.tel, `#${o.id}`].filter(Boolean).join(' '),
          url: '/dashboard/orders', module: 'orders',
        });
        return true;
      }
      case 'invoice': {
        const { data } = await supabase
          .from('factures')
          .select('id, numero, objet, statut, montant_ttc')
          .eq('id', entityId)
          .maybeSingle();
        const f = data as { id: number; numero: string | null; objet: string | null; statut: string | null; montant_ttc: string | null } | null;
        if (!f) return false;
        await upsertSearchEntry({
          tenantId, organizationId, entityType: 'invoice', entityId: f.id,
          title: f.numero ? `Facture ${f.numero}` : `Facture #${f.id}`,
          subtitle: [f.objet, f.statut].filter(Boolean).join(' · ') || null,
          keywords: [f.numero, f.objet, f.statut].filter(Boolean).join(' '),
          url: '/dashboard/finance/factures', module: 'finance',
        });
        return true;
      }
      case 'product': {
        const { data } = await supabase
          .from('product_items')
          .select('id, name, price, currency, status, description')
          .eq('id', entityId)
          .maybeSingle();
        const p = data as { id: number; name: string | null; price: number | null; currency: string | null; status: string | null; description: string | null } | null;
        if (!p) return false;
        await upsertSearchEntry({
          tenantId, organizationId, entityType: 'product', entityId: p.id,
          title: p.name || `Produit #${p.id}`,
          subtitle: p.price != null ? `${p.price} ${p.currency || ''}`.trim() : null,
          description: p.description || null,
          keywords: [p.name, p.status].filter(Boolean).join(' '),
          url: '/dashboard/products', module: 'products',
        });
        return true;
      }
      case 'service': {
        const { data } = await supabase
          .from('services')
          .select('id, titre, description, prix')
          .eq('id', entityId)
          .maybeSingle();
        const s = data as { id: number; titre: string | null; description: string | null; prix: string | null } | null;
        if (!s) return false;
        await upsertSearchEntry({
          tenantId, organizationId, entityType: 'service', entityId: s.id,
          title: s.titre || `Service #${s.id}`,
          subtitle: s.prix || null,
          keywords: [s.titre, s.description].filter(Boolean).join(' '),
          url: '/dashboard/services', module: 'services',
        });
        return true;
      }
      case 'document': {
        const { data } = await supabase
          .from('documents')
          .select('id, name, doc_type')
          .eq('id', entityId)
          .maybeSingle();
        const d = data as { id: number; name: string | null; doc_type: string | null } | null;
        if (!d) return false;
        await upsertSearchEntry({
          tenantId, organizationId, entityType: 'document', entityId: d.id,
          title: d.name || `Document #${d.id}`,
          subtitle: d.doc_type || null,
          keywords: [d.name, d.doc_type].filter(Boolean).join(' '),
          url: '/dashboard/documents', module: 'documents',
        });
        return true;
      }
      case 'accommodation': {
        const { data } = await supabase
          .from('hebergement_properties')
          .select('id, name, city, price, currency')
          .eq('id', entityId)
          .maybeSingle();
        const p = data as { id: number; name: string | null; city: string | null; price: string | null; currency: string | null } | null;
        if (!p) return false;
        await upsertSearchEntry({
          tenantId, organizationId, entityType: 'accommodation', entityId: p.id,
          title: p.name || `Hébergement #${p.id}`,
          subtitle: [p.city, p.price ? `${p.price} ${p.currency || ''}`.trim() : null].filter(Boolean).join(' · ') || null,
          keywords: [p.name, p.city].filter(Boolean).join(' '),
          url: '/dashboard/apps/hebergement', module: 'accommodation',
        });
        return true;
      }
      case 'reservation': {
        const { data } = await supabase
          .from('hebergement_reservations')
          .select('id, guest_name, guest_email, guest_phone, check_in, check_out')
          .eq('id', entityId)
          .maybeSingle();
        const v = data as { id: number; guest_name: string | null; guest_email: string | null; guest_phone: string | null; check_in: string | null; check_out: string | null } | null;
        if (!v) return false;
        await upsertSearchEntry({
          tenantId, organizationId, entityType: 'reservation', entityId: v.id,
          title: v.guest_name || `Réservation #${v.id}`,
          subtitle: [v.check_in, v.check_out].filter(Boolean).join(' → ') || null,
          keywords: [v.guest_name, v.guest_email, v.guest_phone].filter(Boolean).join(' '),
          url: '/dashboard/apps/hebergement', module: 'accommodation',
        });
        return true;
      }
      case 'appointment': {
        const { data } = await supabase
          .from('booking_appointments')
          .select('id, fullname, email, phone, date, time, status')
          .eq('id', entityId)
          .maybeSingle();
        const a = data as { id: number; fullname: string | null; email: string | null; phone: string | null; date: string | null; time: string | null } | null;
        if (!a) return false;
        await upsertSearchEntry({
          tenantId, organizationId, entityType: 'appointment', entityId: a.id,
          title: a.fullname || `Rendez-vous #${a.id}`,
          subtitle: [a.date, a.time].filter(Boolean).join(' · ') || null,
          keywords: [a.fullname, a.email, a.phone].filter(Boolean).join(' '),
          url: '/dashboard/apps/zcalendar', module: 'booking',
        });
        return true;
      }
      default:
        return false;
    }
  } catch {
    // Best-effort : un échec d'indexation ne doit jamais bloquer le métier.
    return false;
  }
}

/** Retire toutes les entrées d'une entité (utile en cas de suppression). */
export async function deindexOne(
  organizationId: number,
  entityType: string,
  entityId: string | number,
): Promise<boolean> {
  try {
    await removeSearchEntry(organizationId, entityType, entityId);
    return true;
  } catch {
    return false;
  }
}